'use client';

import React from 'react';
import {
    Download,
    Calendar,
    Filter,
    Building2,
    ChevronDown,
    FileSpreadsheet,
    FileText as FilePdf
} from 'lucide-react';
import { useRouter, usePathname } from 'next/navigation';
import { getSchoolName, SchoolUnit, validUnits } from '@/lib/data';

interface LaporanHeaderProps {
    title: string;
    description: string;
    activeUnit: SchoolUnit;
    onUnitChange: (unit: SchoolUnit) => void;
}

export default function LaporanHeader({ title, description, activeUnit, onUnitChange }: LaporanHeaderProps) {
    // Group units by base type (paud, sd, smp, smk)
    const baseType = activeUnit.split('-')[0];
    const availableUnits = validUnits.filter(u => u.startsWith(baseType) && u.includes('-'));

    return (
        <section className="space-y-6 animate-in fade-in slide-in-from-top-4 duration-700">
            {/* Main Title & Export */}
            <div className="bg-surface p-8 rounded-4xl border border-border-main shadow-sm relative overflow-hidden group">
                <div className="absolute top-0 right-0 w-96 h-96 bg-primary-soft/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none opacity-50"></div>

                <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div className="space-y-3">
                        <h1 className="text-3xl md:text-4xl font-bold text-foreground tracking-tight">{title}</h1>
                        <p className="text-secondary font-medium flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                            {description}
                        </p>
                    </div>

                    <div className="flex items-center gap-3">
                        <button className="px-5 py-3 bg-surface border border-border-main rounded-2xl text-[10px] font-bold text-foreground hover:bg-primary-soft/50 transition-all flex items-center gap-2 uppercase tracking-widest shadow-sm">
                            <FilePdf className="w-4 h-4 text-error" /> Export PDF
                        </button>
                        <button className="px-5 py-3 bg-surface border border-border-main rounded-2xl text-[10px] font-bold text-foreground hover:bg-primary-soft/50 transition-all flex items-center gap-2 uppercase tracking-widest shadow-sm">
                            <FileSpreadsheet className="w-4 h-4 text-success" /> Export Excel
                        </button>
                    </div>
                </div>
            </div>

            {/* Controls: Unit Selector & Date Filter */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Unit Selector */}
                <div className="col-span-1 lg:col-span-2 bg-surface p-4 rounded-3xl border border-border-main shadow-sm flex items-center gap-4">
                    <div className="p-3 bg-primary-soft text-primary rounded-2xl shadow-sm">
                        <Building2 className="w-6 h-6" />
                    </div>
                    <div className="flex-1 space-y-1">
                        <p className="text-[10px] font-bold text-muted uppercase tracking-widest ml-1">Pilih Unit Sekolah</p>
                        <div className="relative">
                            <select
                                className="w-full pl-0 pr-10 py-1 bg-transparent border-none text-lg font-bold text-foreground focus:ring-0 outline-none appearance-none cursor-pointer"
                                value={activeUnit}
                                onChange={(e) => onUnitChange(e.target.value as SchoolUnit)}
                            >
                                {availableUnits.map(u => (
                                    <option key={u} value={u}>{getSchoolName(u as SchoolUnit)}</option>
                                ))}
                            </select>
                            <ChevronDown className="absolute right-0 top-1/2 -translate-y-1/2 w-5 h-5 text-muted pointer-events-none" />
                        </div>
                    </div>
                </div>

                {/* Period Filter */}
                <div className="bg-surface p-4 rounded-3xl border border-border-main shadow-sm flex items-center gap-4">
                    <div className="p-3 bg-warning-soft text-warning rounded-2xl shadow-sm">
                        <Calendar className="w-6 h-6" />
                    </div>
                    <div className="flex-1 space-y-1">
                        <p className="text-[10px] font-bold text-muted uppercase tracking-widest ml-1">Periode Laporan</p>
                        <div className="relative">
                            <select className="w-full pl-0 pr-10 py-1 bg-transparent border-none text-lg font-bold text-foreground focus:ring-0 outline-none appearance-none cursor-pointer">
                                <option value="2026-02">Februari 2026</option>
                                <option value="2026-01">Januari 2026</option>
                                <option value="2025-12">Desember 2025</option>
                                <option value="custom">Custom Range...</option>
                            </select>
                            <ChevronDown className="absolute right-0 top-1/2 -translate-y-1/2 w-5 h-5 text-muted pointer-events-none" />
                        </div>
                    </div>
                </div>

                {/* Filter Trigger / Status */}
                <div className="bg-surface p-4 rounded-3xl border border-border-main shadow-sm flex items-center gap-4 group cursor-pointer hover:border-primary/30 transition-all">
                    <div className="p-3 bg-secondary-soft text-secondary group-hover:bg-primary-soft group-hover:text-primary rounded-2xl shadow-sm transition-all">
                        <Filter className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                        <p className="text-[10px] font-bold text-muted uppercase tracking-widest ml-1">Filter Lanjut</p>
                        <p className="text-sm font-bold text-foreground">Klik untuk memfilter</p>
                    </div>
                </div>
            </div>
        </section>
    );
}
